import React, { memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';

import {
  StyledTableHeadRow,
  StyledTableBodyRow,
  StyledTableCell,
} from '@ui-elements/styled/StyledTable';
import InputWithLabel from '@ui-parts/InputWithLabel';
import InputValidationErrorTooltip from '@ui-parts/InputValidationErrorTooltip';

import { useTranslation } from 'react-i18next';

import { propTypesNullable, replaceFullToHalf } from '@functional';
import { QUALIFICATION_GENDER_TYPE, QUOTA_GENDER_TYPE, LOCALE_STRING } from '~/constants';
import { PALETTE, Z_INDEX } from '~/assets/styles/base';

const NoDataWrapper = styled.div`
  width: 100%;
  padding: 20px 0;
  background: ${PALETTE.bg.main};
  text-align: center;
`;

function QuotaListTable({
  ages,
  quotas,
  targetSamples,
  qualificationsGender,
  canEdit = false,
  setTargetSamples = null,
  errors = null,
  minWidth = 700,
  form,
  targetSamplesWarnings,
  isAdmin = false,
}) {
  const { t } = useTranslation();

  const handleChange = (e, key) => {
    const adjusted = e.target.value.replace(/[^0-9０-９]+/i, ''); // 全角と半角の数字のみ許可
    setTargetSamples({
      ...targetSamples,
      [key]: adjusted,
    });
    form.setValue(key, adjusted);
  };

  const handleBlur = (value, key) => {
    const adjusted = Number(replaceFullToHalf(value)); // Blurは全角数字を半角に直す
    setTargetSamples({
      ...targetSamples,
      [key]: adjusted,
    });
    form.setValue(key, adjusted);
  };

  const getMinCollect = (key) => {
    return quotas[key]?.minCollect.toLocaleString(LOCALE_STRING.ja);
  };

  const getTargetSample = (key) => {
    return targetSamples[key] === undefined
      ? 0
      : targetSamples[key].toLocaleString(LOCALE_STRING.ja);
  };

  const getColCount = () => {
    return isAdmin ? 1 : 2;
  };

  // agesかquotasのどれか一つでも空なら、もしくはqualificationsGenderがnullならNoData用の表示。
  if (
    !ages ||
    ages.length === 0 ||
    (!isAdmin && Object.keys(quotas).length === 0) ||
    qualificationsGender === null
  ) {
    return !isAdmin ? (
      <InputValidationErrorTooltip
        error={errors ? errors.quotas.invalid : undefined}
        message={errors ? errors.quotas.message : ''}
        dataTestId="SurveyPriceBox"
      >
        <NoDataWrapper>{t('quotas.table.noData')}</NoDataWrapper>
      </InputValidationErrorTooltip>
    ) : null;
  }

  return (
    <TableContainer component="div" data-testid="QuotaListTable">
      <Table sx={{ minWidth }} aria-label="customized table">
        {qualificationsGender === QUALIFICATION_GENDER_TYPE.EACH ? (
          <TableHead>
            <StyledTableHeadRow>
              {/* 2 rows cell */}
              <StyledTableCell rowSpan={2} sx={{ width: '20%' }}>
                {t('quotas.table.header.age')}
              </StyledTableCell>

              {/* 1 row cell */}
              <StyledTableCell colSpan={getColCount()} $bgColored data-testid="MaleHeaderCell">
                {t('quotas.table.header.male')}
              </StyledTableCell>

              <StyledTableCell colSpan={getColCount()} $bgColored data-testid="FemaleHeaderCell">
                {t('quotas.table.header.female')}
              </StyledTableCell>
            </StyledTableHeadRow>

            <StyledTableHeadRow>
              {!isAdmin && (
                <StyledTableCell sx={{ width: '20%' }}>
                  {t('quotas.table.header.minCollect')}
                </StyledTableCell>
              )}
              <StyledTableCell sx={{ width: '20%' }}>
                {t('quotas.table.header.targetSamples')}
              </StyledTableCell>
              {!isAdmin && (
                <StyledTableCell sx={{ width: '20%' }}>
                  {t('quotas.table.header.minCollect')}
                </StyledTableCell>
              )}
              <StyledTableCell sx={{ width: '20%' }}>
                {t('quotas.table.header.targetSamples')}
              </StyledTableCell>
            </StyledTableHeadRow>
          </TableHead>
        ) : (
          <TableHead>
            <StyledTableHeadRow>
              {/* 2 rows cell */}
              <StyledTableCell rowSpan={2} sx={{ width: '30%' }}>
                {t('quotas.table.header.age')}
              </StyledTableCell>

              {/* 1 row cell */}
              {qualificationsGender === QUALIFICATION_GENDER_TYPE.MALE && (
                <StyledTableCell colSpan={getColCount()} $bgColored data-testid="MaleHeaderCell">
                  {t('quotas.table.header.male')}
                </StyledTableCell>
              )}

              {qualificationsGender === QUALIFICATION_GENDER_TYPE.FEMALE && (
                <StyledTableCell colSpan={getColCount()} $bgColored data-testid="FemaleHeaderCell">
                  {t('quotas.table.header.female')}
                </StyledTableCell>
              )}

              {qualificationsGender === QUALIFICATION_GENDER_TYPE.BOTH && (
                <StyledTableCell colSpan={getColCount()} $bgColored data-testid="BothHeaderCell">
                  {t('quotas.table.header.both')}
                </StyledTableCell>
              )}
            </StyledTableHeadRow>

            <StyledTableHeadRow>
              {!isAdmin && (
                <StyledTableCell sx={{ width: '35%' }}>
                  {t('quotas.table.header.minCollect')}
                </StyledTableCell>
              )}

              <StyledTableCell sx={{ width: '35%' }}>
                {t('quotas.table.header.targetSamples')}
              </StyledTableCell>
            </StyledTableHeadRow>
          </TableHead>
        )}
        <TableBody>
          {qualificationsGender === QUALIFICATION_GENDER_TYPE.EACH
            ? // 男女両方の場合
              ages.map((age, i) => (
                <StyledTableBodyRow key={age} $index={i} data-testid={`QuotaListTableRow-${i}`}>
                  <StyledTableCell component="th" scope="row">
                    {age}
                  </StyledTableCell>

                  {!isAdmin && (
                    <StyledTableCell>
                      {getMinCollect(`${age}-${QUOTA_GENDER_TYPE.MALE}`)}
                    </StyledTableCell>
                  )}

                  {canEdit ? (
                    <StyledTableCell
                      $isShowTargetSampleWarning={
                        targetSamplesWarnings[`${age}-${QUOTA_GENDER_TYPE.MALE}`]
                      }
                    >
                      <Controller
                        name={`${age}-${QUOTA_GENDER_TYPE.MALE}`}
                        control={form.control}
                        render={({
                          field: { onChange, onBlur, name, ref },
                          fieldState: { error },
                        }) => {
                          return (
                            <InputValidationErrorTooltip
                              error={Boolean(error)}
                              message={error?.message}
                              zIndex={Z_INDEX.base} // targetSampleWarningとの重なり兼ね合いでZ_INDEX.baseを設定
                              placement="top-end"
                              disablePortal
                              isOneLinerMessage
                              dataTestId={`QuotaTable-${name}`}
                            >
                              <div>
                                <InputWithLabel
                                  key={`${age}-${QUOTA_GENDER_TYPE.MALE}`}
                                  name={name}
                                  width="120px"
                                  value={targetSamples[name]}
                                  error={Boolean(error)}
                                  handleChange={(e) => {
                                    onChange(e);
                                    handleChange(e, name);
                                  }}
                                  handleBlur={(e) => {
                                    onBlur(e);
                                    // Blurで全角数字は半角数字に直す
                                    handleBlur(e.target.value, name);
                                  }}
                                  dataTestId={`QuotaTable-${name}`}
                                  textAlign="right"
                                  inputProps={{ maxLength: 11 }}
                                  ref={ref}
                                />
                              </div>
                            </InputValidationErrorTooltip>
                          );
                        }}
                      />
                    </StyledTableCell>
                  ) : (
                    <StyledTableCell $colored>
                      {getTargetSample(`${age}-${QUOTA_GENDER_TYPE.MALE}`)}
                    </StyledTableCell>
                  )}

                  {!isAdmin && (
                    <StyledTableCell>
                      {getMinCollect(`${age}-${QUOTA_GENDER_TYPE.FEMALE}`)}
                    </StyledTableCell>
                  )}

                  {canEdit ? (
                    <StyledTableCell
                      $isShowTargetSampleWarning={
                        targetSamplesWarnings[`${age}-${QUOTA_GENDER_TYPE.FEMALE}`]
                      }
                    >
                      <Controller
                        name={`${age}-${QUOTA_GENDER_TYPE.FEMALE}`}
                        control={form.control}
                        render={({
                          field: { onChange, onBlur, name, ref },
                          fieldState: { error },
                        }) => {
                          return (
                            <InputValidationErrorTooltip
                              error={Boolean(error)}
                              message={error?.message}
                              zIndex={Z_INDEX.base}
                              placement="top-end"
                              disablePortal
                              isOneLinerMessage
                              dataTestId={`QuotaTable-${name}`}
                            >
                              <div>
                                <InputWithLabel
                                  key={`${age}-${QUOTA_GENDER_TYPE.FEMALE}`}
                                  name={name}
                                  width="120px"
                                  value={targetSamples[name]}
                                  error={Boolean(error)}
                                  handleChange={(e) => {
                                    onChange(e);
                                    handleChange(e, name);
                                  }}
                                  handleBlur={(e) => {
                                    onBlur(e);
                                    // Blurで全角数字は半角に直す
                                    handleBlur(e.target.value, name);
                                  }}
                                  dataTestId={`QuotaTable-${name}`}
                                  textAlign="right"
                                  inputProps={{ maxLength: 11 }}
                                  ref={ref}
                                />
                              </div>
                            </InputValidationErrorTooltip>
                          );
                        }}
                      />
                    </StyledTableCell>
                  ) : (
                    <StyledTableCell $colored>
                      {getTargetSample(`${age}-${QUOTA_GENDER_TYPE.FEMALE}`)}
                    </StyledTableCell>
                  )}
                </StyledTableBodyRow>
              ))
            : // それ以外の場合
              ages.map((age, i) => (
                <StyledTableBodyRow key={age} $index={i} data-testid={`QuotaListTableRow-${i}`}>
                  <StyledTableCell component="th" scope="row">
                    {age}
                  </StyledTableCell>

                  {!isAdmin && (
                    <StyledTableCell>
                      {getMinCollect(`${age}-${qualificationsGender}`)}
                    </StyledTableCell>
                  )}

                  {canEdit ? (
                    <StyledTableCell
                      $isShowTargetSampleWarning={
                        targetSamplesWarnings[`${age}-${qualificationsGender}`]
                      }
                    >
                      <Controller
                        name={`${age}-${qualificationsGender}`}
                        control={form.control}
                        render={({
                          field: { onChange, onBlur, name, ref },
                          fieldState: { error },
                        }) => {
                          return (
                            <InputValidationErrorTooltip
                              error={Boolean(error)}
                              message={error?.message}
                              zIndex={Z_INDEX.base}
                              placement="top-end"
                              disablePortal
                              isOneLinerMessage
                              dataTestId={`QuotaTable-${name}`}
                            >
                              <div>
                                <InputWithLabel
                                  key={`${age}`}
                                  name={name}
                                  width="120px"
                                  value={targetSamples[name]}
                                  error={Boolean(error)}
                                  handleChange={(e) => {
                                    onChange(e);
                                    handleChange(e, name);
                                  }}
                                  handleBlur={(e) => {
                                    onBlur(e);
                                    // Blurで全角数字は半角に直す
                                    handleBlur(e.target.value, name);
                                  }}
                                  dataTestId={`QuotaTable-${name}`}
                                  textAlign="right"
                                  inputProps={{ maxLength: 11 }}
                                  ref={ref}
                                />
                              </div>
                            </InputValidationErrorTooltip>
                          );
                        }}
                      />
                    </StyledTableCell>
                  ) : (
                    <StyledTableCell $colored>
                      {getTargetSample(`${age}-${qualificationsGender}`)}
                    </StyledTableCell>
                  )}
                </StyledTableBodyRow>
              ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

QuotaListTable.propTypes = {
  ages: PropTypes.arrayOf(PropTypes.string).isRequired,
  quotas: PropTypes.shape({
    assignId: PropTypes.number,
    ageFrom: PropTypes.number,
    ageTo: PropTypes.number,
    minCollect: PropTypes.number,
  }).isRequired,
  targetSamples: PropTypes.shape({ targetSamples: PropTypes.number }).isRequired,
  qualificationsGender: propTypesNullable(PropTypes.number.isRequired),
  canEdit: PropTypes.bool,
  setTargetSamples: PropTypes.func,
  errors: PropTypes.shape({
    quotas: PropTypes.shape({
      message: PropTypes.string,
      invalid: PropTypes.bool,
    }),
    targetSamples: PropTypes.shape({
      message: PropTypes.string,
      invalid: PropTypes.bool,
    }),
  }),
  minWidth: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  targetSamplesWarnings: PropTypes.object, // keyが動的の為
  isAdmin: PropTypes.bool,
};

export default memo(QuotaListTable);
